/* eslint-disable no-unreachable */
import { Dispatch, SetStateAction } from 'react';
import * as Sentry from '@sentry/browser';
import TurndownService from 'turndown';
import {
  AuthState,
  ResolvedState,
  SlackChannel, SlackMessageContent, TaskItem,
} from '../../shared/types/types';
import { toastDanger } from '../notifications';
import {
  Block,
  filterSlackChannels,
  SlackNotification,
  sortSlackChannelsAlphabetically,
} from './slackUtils';

import {
  isValidBlock,
  createSlackMessageContent, mapSlackAPIChannelToSlackChannel,
} from './SlackAPIUtils';

import mdToSlackBlocks from './SlackFormatting';
import { dbUpdateSlackNotificationSent } from '../../database/firebaseTasksAPI';
import { logSlackNotification, logSlackUserAction } from '../analytics/eventLogger';
import {
  GET_EVENT, SEND_EVENT, SLACK_CHANNEL, SLACK_DM, SLACK_INTEGRATION, SLACK_NOTIFICATION,
} from '../analytics/enums';
import { DropdownItem } from '../../shared/components/dropdown/utils';
import { REJECTED, RESOLVED } from '../enums';
import ConsoleImproved from '../../shared/classes/ConsoleImproved';
import CloudFunctions from '../../database/CloudFunctions';

const turndownService = new TurndownService();

const slackCoreAPISendMessage = (
  messageContent: SlackMessageContent,
  accessToken: string,
) => CloudFunctions().sendMessageToSlackV3({ accessToken, messageContent })
  .then((response) => response.data)
  .catch((error) => {
    console.log('Got NO response from slackAPISendMessage');
    console.log(error);
    throw error;
  });

export default slackCoreAPISendMessage;

export const slackCoreSendMessages = (
  authState: AuthState,
  channels: SlackChannel[],
  accessToken: string,
  notes: string,
) => {
  const promises = channels.map((channel) => {
    const convertedBlocks = coreConvertToSlackBlocks(notes);
    const messageContent = createSlackMessageContent(channel, convertedBlocks);
    return slackCoreAPISendMessage(messageContent, accessToken);
  });
  return Promise.all(promises).then((responses) => {
    logSlackUserAction(authState.userId, SEND_EVENT, SLACK_CHANNEL, channels.length);
    console.log(responses);
    return responses;
  });
};

export const coreConvertToSlackBlocks = (notes: string) => {
  try {
    const mdNotes = turndownService.turndown(notes);
    ConsoleImproved.log('mdNotes', mdNotes);
    const slackBlocks = mdToSlackBlocks(mdNotes);
    ConsoleImproved.log('slackBlocks', slackBlocks);
    return slackBlocks;
  } catch (error) {
    Sentry.captureException({
      integration: SLACK_INTEGRATION, message: 'formatting error', error, htmlContent: notes,
    });
    toastDanger('Something went wrong while parsing the notes to slack');
    console.error('Error while parsing notes to slack', notes);
    return [];
  }
};

export const slackCoreAPISendNotificationForTaskUpdate = (
  task: TaskItem,
  context: AuthState,
  updatedSection: string,
  newDueDate?: string,
  newTitle?: string,
) => {
  if (task.assignee.data.email === context.email) return;
  if (!task.assignee.external.slack.hasEnabledSlack) return;
  if (!task.assignee.external.slack.notifications.taskUpdated) return;
  const updatedTaskDescription = task.data.description;
  const taskCreatorNames = task.data.reporter.name;
  const updaterNames = `${context.firstName} ${context.lastName}`;
  const taskTitle = newTitle?.length ? newTitle : task.data.title;
  const meeting = task.meeting.name;

  const dueDateText = generateDueDateText(task, newDueDate);

  const blocks = [
    {
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*${updaterNames}* updated the ${updatedSection} of a task assigned to you \t ${dueDateText} \n*Task:* ${taskTitle} ${updatedTaskDescription.length ? `\n*Description* ${updatedTaskDescription}` : ''} \n :bust_in_silhouette: *Created by:* ${taskCreatorNames} ${meeting.length ? `\t :virtual-meeting: *Meeting*: ${meeting}` : ''} `,
      },
    },
  ];
  if (!isValidBlock(blocks)) return;
  const dataProps = {
    email: task.assignee.data.email,
    blocks,
    context,
  };
  CloudFunctions().sendSlackNotification(dataProps)
    .then(() => {
      console.log('slack notification sent successfully');
      // TODO - Not sure which action we should use. SEND / CREATE / UPDATE for notifications?
      logSlackNotification(context.userId, SEND_EVENT, SLACK_NOTIFICATION, updatedSection);
    })
    .catch((error) => {
      console.log(error);
      toastDanger('Something has went wrong with the slack task updated notification');
      Sentry.captureException(error);
    });
};

export const slackCoreAPISendNotificationForTaskCreate = (
  task: TaskItem,
  context: AuthState,
) => {
  if (task.assignee.data.email === context.email) return;
  if (!task.assignee.external.slack.hasEnabledSlack) return;
  if (!task.assignee.external.slack.notifications.taskCreated) return;
  const meeting = task.meeting.name;
  const { description } = task.data;
  const taskCreatorNames = task.data.reporter.name;
  const dueDateText = generateDueDateText(task);

  const blocks = [
    {
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*${taskCreatorNames}* created a task assigned to you \t ${dueDateText}\n* Name:* ${task.data.title} ${description.length ? `\n*Description* ${description}` : ''} ${meeting.length ? `\n :virtual-meeting: *Meeting*: ${meeting}` : ''}`,
      },
    },
  ];
  if (!isValidBlock(blocks)) return;
  const dataType: SlackNotification = {
    email: task.assignee.data.email,
    blocks,
    context,
  };
  console.log('DataType sent to slack');
  console.log(dataType);
  CloudFunctions().sendSlackNotification(dataType)
    .then((result) => {
      logSlackNotification(context.userId, SEND_EVENT, SLACK_NOTIFICATION, 'create');
      return result;
    })
    .catch((error) => {
      console.error(error);
      toastDanger('Something has went wrong with the slack new task notification');
      Sentry.captureException(error);
    });
};

export const slackCoreAPISendNotificationForTaskDelete = (
  task: TaskItem,
  context: AuthState,
) => {
  if (task.assignee.data.email === context.email) return;
  if (!task.assignee.external.slack.hasEnabledSlack) return;
  if (!task.assignee.external.slack.notifications.taskDeleted) return;
  const meeting = task.meeting.name;
  const { description } = task.data;
  const deleterNames = `${context.firstName} ${context.lastName}`;
  const taskCreatorNames = task.data.reporter.name;
  const dueDateText = generateDueDateText(task);

  const blocks: Block[] = [
    {
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `*${deleterNames}* deleted a task assigned to you \t ${dueDateText} \n*Task Name:* ${task.data.title} \n :bust_in_silhouette: *Created by:* ${taskCreatorNames} ${description.length ? `\n*Description* ${description}` : ''} ${meeting.length ? `\n :virtual-meeting: *Meeting*: ${meeting}` : ''}`,
      },
    },
  ];
  if (!isValidBlock(blocks)) return;
  const deletedTaskData: SlackNotification = {
    email: task.assignee.data.email,
    blocks,
    context,
  };
  CloudFunctions().sendSlackNotification(deletedTaskData)
    .then((result) => {
      logSlackNotification(context.userId, SEND_EVENT, SLACK_NOTIFICATION, 'delete');
      return result;
    })
    .catch((error) => {
      console.log(error);
      Sentry.captureException(error);
    });
};

export const slackCoreAPISendNotificationForTaskOverdue = (
  task: TaskItem,
  context: AuthState,
) => {
  if (!task.assignee.external.slack.notifications.taskOverdue) return;
  if (task.integrations.slack.isOverdueNotificationSent) return;
  const taskDescription = task.data.description;
  const meeting = task.meeting.name;
  const taskCreatorNames = task.data.reporter.name;
  const dueDateText = generateDueDateText(task);

  const blocks = [
    {
      type: 'section',
      text: {
        type: 'mrkdwn',
        text: `You have an overdue task \t ${dueDateText} \n *Task Due:* ${task.data.title} ${taskDescription.length ? `\n*Description* ${taskDescription}` : ''} \n :bust_in_silhouette: *Created by:* ${taskCreatorNames} ${meeting.length ? `\n :virtual-meeting: *Meeting*: ${meeting}` : ''}`,
      },
    },
  ];
  if (!isValidBlock(blocks)) return;
  const overdueTaskData: SlackNotification = {
    email: task.assignee.data.email,
    blocks,
    context,
  };
  CloudFunctions().sendSlackNotification(overdueTaskData)
    .then((result) => {
      dbUpdateSlackNotificationSent(task);
      logSlackNotification(context.userId, SEND_EVENT, SLACK_NOTIFICATION, 'overdue');
      return result;
    })
    .catch((error) => {
      console.log(error);
      toastDanger('Something has went wrong with the slack task overdue notification');
      Sentry.captureException(error);
    });
};

export const slackCoreGetChannelNames = async (
  authState: AuthState,
  accessToken: string,
  setSlackChannels: Dispatch<SetStateAction<DropdownItem[]>>,
  setIsResolved: Dispatch<SetStateAction<ResolvedState>>,
) => {
  CloudFunctions().getAllSlackChannelsV6(accessToken)
    .then((response: any) => {
      console.log('Response from getAllSlackChannelsV5', { response });
      const channelsFromCF = response?.data ?? [];
      const channels = channelsFromCF.filter(filterSlackChannels);
      const slackChannels = channels.map(
        (channel: any) => mapSlackAPIChannelToSlackChannel(channel, SLACK_CHANNEL),
      ) ?? [];
      const sortedChannels = sortSlackChannelsAlphabetically(slackChannels);
      logSlackUserAction(authState.userId, GET_EVENT, SLACK_CHANNEL, slackChannels.length);
      if (sortedChannels.length === 0) return;
      setSlackChannels(sortedChannels);
      setIsResolved(RESOLVED);
    })
    .catch((error) => {
      setIsResolved(REJECTED);
      console.log(error);
      toastDanger('Failed to get Slack channels', 'Please try refreshing the page');
      Sentry.captureException(error);
    });
};

export const slackCoreGetDirectMessages = async (
  authState: AuthState,
  accessToken: string,
  setSlackChannels: Dispatch<SetStateAction<DropdownItem[]>>,
  setIsResolved: Dispatch<SetStateAction<ResolvedState>>,
) => {
  if (accessToken.length === 0) return;
  CloudFunctions()
    .slackGetDirectMessagesV4({ accessToken })
    .then((response: any) => {
      console.log('Response from slackGetDirectMessages', { response });
      const directMessages = response?.data ?? [];
      const channelsWithoutBots = directMessages.filter(
        (channel: any) => !channel?.is_bot,
      );
      const channelsWithoutDeactivated = channelsWithoutBots.filter(
        (channel: any) => !channel?.deleted,
      );
      const slackChannels = channelsWithoutDeactivated.map(mapSlackAPIChannelToSlackChannel) ?? [];
      const sortedSlackChannels = sortSlackChannelsAlphabetically(slackChannels);
      logSlackUserAction(authState.userId, GET_EVENT, SLACK_DM, slackChannels.length);
      if (sortedSlackChannels.length === 0) return;
      setSlackChannels(sortedSlackChannels);
      setIsResolved(RESOLVED);
    })
    .catch((error) => {
      setIsResolved(REJECTED);
      console.log(error);
      toastDanger('Failed to get direct Slack messages', 'Please try refreshing the page');
      Sentry.captureException(error);
    });
};

/**
 * If there exist a newDueDate, it will generate a new message using that date
 *
 * If not, will check if there exist an old due date, and if so generate a message using that date
 *
 * If no due dates, then will return an empty string
 *
 * @returns {string}
 * `(Date Emoji) Due: Tue Jan 25`
 */
const generateDueDateText = (task: TaskItem, newDueDate?: string): string => {
  if (typeof newDueDate === 'undefined' || newDueDate.length === 0) {
    // No new due date was provided
    // Evaluating if there is an existing due date
    if (task.date.dueDate.type !== 'date') return '';
    if (task.date.dueDate.date.date.length === 0) return '';
    const dueDate = new Date(task.date.dueDate.date.date);
    if (dueDate?.toISOString()?.length === 0 ?? true) return '';
    const text = `:date: Due: ${dueDate.toDateString()}`;
    return text;
  }
  // New due date provided
  const dueDate = new Date(newDueDate);
  return `:date: Due: ${dueDate.toDateString()}`;
};
